import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { AssetRow, AssetStatus, buildAssetRows, UNASSIGNED_AREA } from '../listHelpers';
import AssetStatusPill from './AssetStatusPill';

interface AssetCardViewProps {
  templates: any[];
  /** Groups and sub-assets behind each template, for the rollup counts only. */
  groups?: any[];
  items?: any[];
  statusByTemplateId?: Map<number, AssetStatus>;
  workOrdersByTemplateId?: Map<number, number>;
  selectedRowKey?: string | null;
  onSelectRow: (row: AssetRow) => void;
}

/**
 * Tile layout of the site's templates: status and area on top, then the
 * equipment photo, name, open work orders and description.
 */
const AssetCardView: React.FC<AssetCardViewProps> = ({
  templates,
  groups,
  items,
  statusByTemplateId,
  workOrdersByTemplateId,
  selectedRowKey,
  onSelectRow,
}) => {
  const intl = useIntl();

  const cards = useMemo(
    () =>
      buildAssetRows(templates, [], [], {
        groups,
        items,
        statusByTemplateId,
        workOrdersByTemplateId,
      }),
    [templates, groups, items, statusByTemplateId, workOrdersByTemplateId]
  );

  if (!cards.length) {
    return (
      <div className="assets-page__empty">
        <p>{intl.formatMessage({ id: 'assets.list.empty' })}</p>
      </div>
    );
  }

  return (
    <div className="asset-card-view">
      {cards.map((card) => (
        <button
          type="button"
          key={card.key}
          className={`asset-card ${selectedRowKey === card.key ? 'asset-card--selected' : ''}`}
          onClick={() => onSelectRow(card)}
        >
          <span className="asset-card__top">
            <AssetStatusPill status={card.status} />
            {card.area !== UNASSIGNED_AREA && (
              <span className="asset-card__area" title={card.area}>
                <i className="uil uil-layer-group" />
              </span>
            )}
          </span>

          <span className="asset-card__image">
            {card.imageUrl ? (
              <img src={card.imageUrl} alt={card.name} loading="lazy" />
            ) : (
              <i className="uil uil-image" />
            )}
          </span>

          <span className="asset-card__name">{card.name}</span>

          <span
            className={`asset-card__meta ${card.workOrders > 0 ? '' : 'asset-card__meta--none'}`}
          >
            {card.workOrders > 0
              ? intl.formatMessage({ id: 'assets.card.workOrders' }, { count: card.workOrders })
              : intl.formatMessage({ id: 'assets.card.noWorkOrders' })}
          </span>

          {card.description && <span className="asset-card__description">{card.description}</span>}
        </button>
      ))}
    </div>
  );
};

export default AssetCardView;
